import { useMemo } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';

import type { ThirdPartiesCmp_SomeOrganization } from '../queries/some-organizations.query';

import { useFindAllEnabledExternalOrganizationsQuery } from './useFindAllEnabledExternalOrganizationsQuery.hook';
import { useFindAllDisabledExternalOrganizationsQuery } from './useFindAllDisabledExternalOrganizationsQuery.hook';
import { useFindAllDeletedExternalOrganizationsQuery } from './useFindAllDeletedExternalOrganizationsQuery.hook';

interface UnionResult {
  readonly data:
    | Readonly<{
        findSomeOrganizations: readonly ThirdPartiesCmp_SomeOrganization[];
      }>
    | undefined;
  readonly isLoading: boolean;
  readonly isSuccess: boolean;
}

export const useFindAllExternalOrganizationsQuery = (
  enabled = true,
): UnionResult => {
  const deletedQuery = useFindAllDeletedExternalOrganizationsQuery(enabled);
  const disabledQuery = useFindAllDisabledExternalOrganizationsQuery(enabled);
  const enabledQuery = useFindAllEnabledExternalOrganizationsQuery(enabled);

  const memoizedResult = useMemo(() => {
    const deletedData = deletedQuery.data?.findSomeOrganizations;
    const disabledData = disabledQuery.data?.findSomeOrganizations;
    const enabledData = enabledQuery.data?.findSomeOrganizations;

    const data =
      deletedData || disabledData || enabledData
        ? {
            findSomeOrganizations: [
              ...(deletedData ?? EMPTY_ARRAY),
              ...(disabledData ?? EMPTY_ARRAY),
              ...(enabledData ?? EMPTY_ARRAY),
            ],
          }
        : undefined;

    const isLoading =
      deletedQuery.isLoading ||
      disabledQuery.isLoading ||
      enabledQuery.isLoading;
    const isSuccess =
      deletedQuery.isSuccess &&
      disabledQuery.isSuccess &&
      enabledQuery.isSuccess;

    return { data, isLoading, isSuccess };
  }, [
    deletedQuery.data?.findSomeOrganizations,
    deletedQuery.isLoading,
    deletedQuery.isSuccess,
    disabledQuery.data?.findSomeOrganizations,
    disabledQuery.isLoading,
    disabledQuery.isSuccess,
    enabledQuery.data?.findSomeOrganizations,
    enabledQuery.isLoading,
    enabledQuery.isSuccess,
  ]);

  return memoizedResult;
};
